import { SelectionList, Tooltip } from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import {
  SearchFilterConditionCustomAttribute,
  SearchFilterConditionCustomAttributeField,
} from 'lib-shared-types-api';
import orderBy from 'lodash/orderBy';
import { memo, useCallback, useMemo } from 'react';

import {
  CustomFieldAttribute,
  CustomFieldEntity,
  customFieldsApi,
} from 'apis/customFields';
import { customAttributeTypeAttributes } from 'app/views/layout/views/settings/views/customAttributes/components/table/utils/customAttributeTypeAttributes';

import { fieldOperators } from '../../../../utils/definitions';
import { ConditionFieldLayout } from '../../../condition/components/field/components/layout/conditionFieldLayout';
import styles from '../../filterAddCondition.module.scss';

type FilterAddConditionCustomAttributesProps = {
  /** The entity of custom fields to fetch and render. */
  entityId: CustomFieldEntity;
  /** Whether the parent dropdown is open. */
  isOpen: boolean;
  /** The current search term. */
  debouncedSearchTerm: string;
};

export const FilterAddConditionCustomAttributes = memo(
  function FilterAddConditionCustomAttributes({
    entityId,
    debouncedSearchTerm,
    isOpen,
  }: FilterAddConditionCustomAttributesProps): JSX.Element | null {
    const { customAttributes, isLoading } =
      customFieldsApi.useReadAttributesQuery(
        { entityId },
        {
          skip: !isOpen,
          selectFromResult: ({ data, ...rest }) => ({
            ...rest,
            customAttributes: data?.data.attributes ?? [],
          }),
        },
      );

    const getCustomAttributeFilterConditionValue = useCallback(
      (
        attribute: CustomFieldAttribute,
      ): SearchFilterConditionCustomAttribute => {
        const field: SearchFilterConditionCustomAttributeField = `${entityId}_custom_attribute`;

        return {
          field,
          operator: fieldOperators[field].defaultOperator,
          values: [],
          attribute_id: attribute.id,
        };
      },
      [entityId],
    );

    /**
     * The list of custom attribute available to filter by.
     *
     * These are first filtered according to the search criteria if any, and
     * finally ordered so that deactivated attributes are at the bottom.
     */
    const filteredCustomAttributes = useMemo(() => {
      const fuse = new Fuse<CustomFieldAttribute>(customAttributes, {
        keys: ['label', 'identifier'],
        threshold: 0.2,
        distance: 200,
      });

      const filteredAttributes = [
        ...(debouncedSearchTerm
          ? fuse.search(debouncedSearchTerm).map(({ item }) => item)
          : customAttributes),
      ];

      return orderBy<CustomFieldAttribute>(
        filteredAttributes,
        'is_deactivated',
        'asc',
      );
    }, [customAttributes, debouncedSearchTerm]);

    if (isLoading) return <ConditionFieldLayout.Loading />;
    if (!filteredCustomAttributes.length) return null;

    return (
      <SelectionList.Section<SearchFilterConditionCustomAttribute>
        key={entityId}
        title={
          entityId === 'conversation'
            ? 'Conversation attributes'
            : 'Contact attributes'
        }
      >
        {filteredCustomAttributes.map((attribute) => (
          <Tooltip
            className={styles.tooltip}
            key={attribute.id}
            head={attribute.label}
            content={attribute.description}
            disabled={!attribute.description}
            side="left"
            wrap="none"
          >
            <ConditionFieldLayout.Item<SearchFilterConditionCustomAttribute>
              value={getCustomAttributeFilterConditionValue(attribute)}
              label={attribute.label}
              icon={customAttributeTypeAttributes[attribute.type].icon}
              highlight={debouncedSearchTerm}
              noFixedSelectionStateSpace
              textProps={{
                color: attribute.is_deactivated ? 'tertiary' : undefined,
              }}
            />
          </Tooltip>
        ))}
      </SelectionList.Section>
    );
  },
);
